import React, { useState, useCallback, useMemo } from "react";
import {
  Container,
  Typography,
  Box,
  Button,
  TextField,
  InputAdornment,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import Header from "../components/Admin/header/Header";
import Sidebar from "../components/Admin/Sidebar/Sidebar";
import { Search } from "lucide-react";
import {
  AddCircleOutlineOutlined,
  MoreVert,
  ExpandMore,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useGetApi } from "../hooks/useGetApi";
import Loader from "../components/Loader/loader";
import ErrorMessage from "../components/ErrorMessage/errorMessage";
import {
  getTransactionTypeApi,
  deleteTransactionTypeByIdApi,
  deleteBankAccountApi,
} from "../services/transaction-type.service";
import TransactionTypeModal from "../components/item_master/modal/transaction-type-modal";
import DeleteConfirmationDialog from "../components/DeleteConfirmationDialog";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { toast } from "react-toastify";
import {
  DEFAULT_LIMIT,
  DEFAULT_ROWS_PER_PAGE_OPTIONS,
} from "../utils/constants";

const MasterTransactionType = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [dependencies, setDependencies] = useState(null);

  // Bank deletion states
  const [bankDeleteDialogOpen, setBankDeleteDialogOpen] = useState(false);
  const [bankDeleteLoading, setBankDeleteLoading] = useState(false);
  const [selectedBankForDelete, setSelectedBankForDelete] = useState(null);

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: DEFAULT_LIMIT,
  });
  const [sortModel, setSortModel] = useState([
    { field: "transactionMode", sort: "asc" },
  ]);
  const [expandedPanels, setExpandedPanels] = useState({});

  const handleAccordionChange = (transactionIndex, transactionTypeIndex, bankIndex) => {
    const panelKey = `${transactionIndex}-${transactionTypeIndex}-${bankIndex}`;
    setExpandedPanels((prev) => ({
      ...prev,
      [panelKey]: !prev[panelKey],
    }));
  };

  const handleModalOpen = () => {
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedRow(null);
  };

  const handleEdit = (rowData) => {
    setSelectedRow(rowData);
    handleModalOpen();
  };

  const handleClose = () => {
    navigate(-1); // Navigate back to previous page
  };

  const handleMenuClick = (event, transaction) => {
    setAnchorEl(event.currentTarget);
    setSelectedTransaction(transaction);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedTransaction(null);
  };

  const handleDelete = () => {
    setDependencies(null); // Reset dependencies
    setDeleteDialogOpen(true);
    setAnchorEl(null); // Close menu but keep selectedTransaction
  };

  const handleDeleteConfirm = async () => {
    if (!selectedTransaction?._id) {
      toast.error("No transaction selected for deletion");
      setDeleteDialogOpen(false);
      return;
    }

    setDeleteLoading(true);
    try {
      const response = await deleteTransactionTypeByIdApi(selectedTransaction._id);
      if (response?.code === 200) {
        toast.success("Transaction type deleted successfully");
        setDeleteDialogOpen(false);
        setSelectedTransaction(null);
        setDependencies(null);
        refetch();
      } else if (response?.code === 400 && response?.dependencies) {
        // Handle dependency error - show dependencies in dialog
        setDependencies(response.dependencies);
        toast.warning(response?.message || "Cannot delete due to dependencies");
      } else {
        toast.error(response?.message || "Failed to delete transaction type");
        setDeleteDialogOpen(false);
        setSelectedTransaction(null);
        setDependencies(null);
      }
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Failed to delete transaction type");
      setDeleteDialogOpen(false);
      setSelectedTransaction(null);
      setDependencies(null);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setSelectedTransaction(null);
    setDependencies(null);
  };

  // Bank deletion handlers
  const handleBankDelete = (transactionType, bankIndex) => {
    setSelectedBankForDelete({
      transactionType,
      bankIndex,
      bankName: transactionType.bankDetails[bankIndex]?.bankName || 'Unknown Bank'
    });
    setBankDeleteDialogOpen(true);
  };

  const handleBankDeleteConfirm = async () => {
    if (!selectedBankForDelete) {
      toast.error("No bank account selected for deletion");
      setBankDeleteDialogOpen(false);
      return;
    }

    setBankDeleteLoading(true);
    try {
      const response = await deleteBankAccountApi(
        selectedBankForDelete.transactionType._id,
        selectedBankForDelete.bankIndex
      );
      
      if (response?.code === 200) {
        toast.success("Bank account deleted successfully");
        setBankDeleteDialogOpen(false);
        setSelectedBankForDelete(null);
        refetch();
      } else {
        toast.error(response?.message || "Failed to delete bank account");
        setBankDeleteDialogOpen(false);
        setSelectedBankForDelete(null);
      }
    } catch (error) {
      console.error("Bank delete error:", error);
      toast.error("Failed to delete bank account");
      setBankDeleteDialogOpen(false);
      setSelectedBankForDelete(null);
    } finally {
      setBankDeleteLoading(false);
    }
  };

  const handleBankDeleteCancel = () => {
    setBankDeleteDialogOpen(false);
    setSelectedBankForDelete(null);
  };

  const {
    dataList: transactionList,
    isLoading,
    isError,
    refetch,
  } = useGetApi({
    apiFunction: getTransactionTypeApi,
    params: { search },
    dependencies: [search],
    debounceDelay: 500,
  });

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const flattenedRows = useMemo(() => {
    const list = transactionList || [];
    const rows = [];
    list.forEach((detail, ti) => {
      (detail.transactionTypes || []).forEach((opt, oi) => {
        rows.push({
          id: opt._id || `t${ti}-o${oi}`,
          _id: opt._id,
          transactionMode: detail.transactionMode || "-",
          paymentMethod: opt.paymentMethod || "-",
          bankCount: (opt.bankDetails || []).length,
          _original: opt,
        });
      });
    });
    return rows;
  }, [transactionList]);

  const searchLower = String(search || "").toLowerCase();
  const filteredRows = searchLower
    ? flattenedRows.filter(
        (r) =>
          String(r.transactionMode || "").toLowerCase().includes(searchLower) ||
          String(r.paymentMethod || "").toLowerCase().includes(searchLower)
      )
    : flattenedRows;

  const sortField = sortModel[0]?.field || "transactionMode";
  const sortOrder = sortModel[0]?.sort || "asc";
  const sortedRows = [...filteredRows].sort((a, b) => {
    const cmp = String(a[sortField] ?? "").localeCompare(
      String(b[sortField] ?? ""),
      undefined,
      { numeric: true }
    );
    return sortOrder === "asc" ? cmp : -cmp;
  });
  const totalFiltered = sortedRows.length;
  const pageSize = paginationModel.pageSize;
  const page = paginationModel.page;
  const rows =
    totalFiltered > pageSize
      ? sortedRows.slice(page * pageSize, page * pageSize + pageSize)
      : sortedRows;

  const getColumns = useCallback(
    () => [
      {
        field: "transactionMode",
        headerName: "Transaction Mode",
        flex: 1,
        minWidth: 140,
        headerAlign: "left",
        align: "left",
        sortable: true,
      },
      {
        field: "paymentMethod",
        headerName: "Payment Method",
        flex: 1,
        minWidth: 140,
        headerAlign: "left",
        align: "left",
        sortable: true,
      },
      {
        field: "bankCount",
        headerName: "Bank Count",
        flex: 0.5,
        minWidth: 90,
        headerAlign: "center",
        align: "center",
        sortable: true,
      },
      {
        field: "actions",
        headerName: "Actions",
        flex: 0,
        width: 80,
        minWidth: 70,
        headerAlign: "right",
        align: "right",
        sortable: false,
        renderCell: (params) => (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleMenuClick(e, params.row._original);
            }}
            sx={{ color: "#5D6A82" }}
          >
            <MoreVert />
          </IconButton>
        ),
      },
    ],
    []
  );

  const dataGridSx = {
    border: "none",
    "& .MuiDataGrid-root": { border: "none", width: "100%" },
    "& .MuiDataGrid-cell": {
      borderBottom: "1px solid #f0f0f0",
      padding: isMobile ? "4px" : "8px",
      fontSize: isMobile ? "12px" : "14px",
    },
    "& .MuiDataGrid-columnHeaders": {
      backgroundColor: "#f5f5f5",
      borderBottom: "2px solid #e0e0e0",
    },
    "& .MuiDataGrid-columnHeader": {
      backgroundColor: "#f5f5f5",
      fontWeight: "bold",
    },
    "& .MuiDataGrid-row": {
      "&:hover": { backgroundColor: "#f5f5f5" },
      "&:nth-of-type(odd)": { backgroundColor: "#fafafa" },
      "&:nth-of-type(even)": { backgroundColor: "white" },
    },
    "& .MuiDataGrid-footerContainer": {
      borderTop: "2px solid #e0e0e0",
      backgroundColor: "#f9f9f9",
    },
  };

  return (
    <Container maxWidth={false} disableGutters>
      <Header />
      <Box sx={{ display: "flex" }}>
        <Box>
          <Sidebar />
        </Box>
        <Box sx={{ width: "100%", pr: "90px", paddingBottom: 2 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(-1)}
            sx={{
              color: "#5D6A82",
              textTransform: "none",
              fontSize: "16px",
              fontWeight: "bold",
              padding: 0,
              transition: "none !important",
              mt: 2,
            }}
          >
            Back
          </Button>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              paddingTop: 2,
            }}
          >
            <Typography sx={{ color: "rgba(93, 106, 130, 1)", fontSize: 20 }}>
              Transaction Type Master
            </Typography>
          </Box>
          <Box
            sx={{
              my: "20px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              paddingBottom: 2,
            }}
          >
            <TextField
              placeholder="Search by Name"
              size="small"
              sx={{ width: "300px" }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <select
                        className="uid-select"
                        readOnly
                        style={{
                          border: "none",
                          outline: "none",
                          background: "transparent",
                          cursor: "pointer",
                          color: "#333",
                          fontWeight: "500",
                        }}
                      >
                        <option value="name">Name</option>
                      </select>
                      <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                      <Search />
                    </InputAdornment>
                  ),
                },
              }}
              value={search || ""}
              onChange={(e) => setSearch(e.target.value || "")}
            />
            <Button
              variant="contained"
              sx={{
                textTransform: "none",
                display: "flex",
                gap: 0.5,
                minWidth: "150px",
              }}
              onClick={handleModalOpen}
            >
              <AddCircleOutlineOutlined />
              New Transaction Type
            </Button>
          </Box>

          {isLoading ? (
            <Loader />
          ) : isError ? (
            <ErrorMessage />
          ) : (
            <Box
              sx={{
                height: {
                  xs: "calc(100vh - 300px)",
                  sm: "calc(100vh - 320px)",
                  md: "calc(100vh - 350px)",
                },
                width: "100%",
                minHeight: "300px",
                overflow: "hidden",
              }}
            >
              <DataGrid
                rows={rows}
                columns={getColumns()}
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                pageSizeOptions={DEFAULT_ROWS_PER_PAGE_OPTIONS}
                rowCount={totalFiltered}
                sortModel={sortModel}
                onSortModelChange={setSortModel}
                disableRowSelectionOnClick
                sx={dataGridSx}
                components={{
                  NoRowsOverlay: () => (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "100%",
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        No transaction types found
                      </Typography>
                    </Box>
                  ),
                }}
              />
            </Box>
          )}
          {/* modals */}
          <TransactionTypeModal
            open={modalOpen}
            onClose={handleModalClose}
            refetch={refetch}
            selectedRow={selectedRow}
          />

          {/* Three dots menu */}
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            <MenuItem onClick={() => { handleEdit(selectedTransaction); handleMenuClose(); }}>
              Edit
            </MenuItem>
            <MenuItem onClick={handleDelete} sx={{ color: 'red' }}>
              Delete
            </MenuItem>
          </Menu>

          {/* Delete confirmation dialog */}
          <DeleteConfirmationDialog
            open={deleteDialogOpen}
            onClose={handleDeleteCancel}
            onConfirm={handleDeleteConfirm}
            isLoading={deleteLoading}
            itemName={selectedTransaction?.name || selectedTransaction?.paymentMethod || "this transaction type"}
            dependencies={dependencies}
          />

          {/* Bank Delete confirmation dialog */}
          <DeleteConfirmationDialog
            open={bankDeleteDialogOpen}
            onClose={handleBankDeleteCancel}
            onConfirm={handleBankDeleteConfirm}
            isLoading={bankDeleteLoading}
            itemName={`bank account "${selectedBankForDelete?.bankName || 'Unknown Bank'}"`}
          />
        </Box>
      </Box>
    </Container>
  );
};

export default MasterTransactionType;
